import { Box, Button, Checkbox, FormControlLabel, Modal, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { modalStyle } from '@/components/modalStyle';
import { PlanUnit, TrainingPlan } from '@/generated';
import { copyWorkoutUnit, createWorkoutUnit, updateWorkoutExerciseMetrics } from './creationUtils';
import { useUpdatePlan } from '@/hooks/plan-mutation.hooks';
import moment from 'moment';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useAllExercisesQuery, useDeckExercisesQueries } from '@/hooks/query-hooks';

export const CopyWorkoutUnitModal = (props: { show: boolean; setShow: (show: boolean) => void; plan: TrainingPlan; workoutUnit?: PlanUnit }) => {
  const { plan, workoutUnit } = props;
  const lastSortKey = plan.units.reduce((p, c) => (p > c.sortKey ? p : c.sortKey), 0) ?? 0;
  const [editWorkoutUnit, setEditWorkoutUnit] = useState<PlanUnit>(createWorkoutUnit('', lastSortKey + 1));
  const [shouldUpdateExerciseMetrics, setShouldUpdateExerciseMetrics] = useState(true);
  const updatePlanMutation = useUpdatePlan();
  const { exercisesByDeckId } = useDeckExercisesQueries(workoutUnit?.workouts.map((w) => w.deckId));
  const { exercises } = useAllExercisesQuery(true);

  return (
    <Modal
      open={props.show}
      onClose={() => {
        setShouldUpdateExerciseMetrics(true);
        props.setShow(false);
      }}
    >
      <Box sx={modalStyle}>
        <Typography variant="h4">
          Copy {props.workoutUnit?.name} -&gt; {editWorkoutUnit.name}
        </Typography>
        <Stack sx={{ gap: 1, mt: 2 }}>
          <DateTimePicker
            ampm={false}
            value={moment(editWorkoutUnit.trainingDateIso)}
            onChange={(v) => {
              if (v) setEditWorkoutUnit({ ...editWorkoutUnit, trainingDateIso: v.toISOString() });
            }}
          />
          <TextField
            sx={{ my: 2 }}
            multiline
            label={'Copied Workout Name'}
            value={editWorkoutUnit.name}
            onChange={(e) => {
              setEditWorkoutUnit({ ...editWorkoutUnit, name: e.currentTarget.value });
            }}
          />
          <TextField
            sx={{}}
            label={'Workout Description'}
            multiline
            minRows={3}
            value={editWorkoutUnit.description}
            onChange={(e) => {
              setEditWorkoutUnit({ ...editWorkoutUnit, description: e.target.value });
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={shouldUpdateExerciseMetrics}
                onChange={(event) => setShouldUpdateExerciseMetrics(event.target.checked)}
              />
            }
            label="Update Exercise Metrics"
          />
          &nbsp;
        </Stack>
        <Button
          onClick={async () => {
            if (props.workoutUnit) {
              const copy = copyWorkoutUnit(editWorkoutUnit, props.workoutUnit);
              if (shouldUpdateExerciseMetrics) {
                for (const wu of copy.workouts) updateWorkoutExerciseMetrics(wu, exercisesByDeckId[wu.deckId] ?? [], exercises ?? []);
              }

              await updatePlanMutation.mutateAsync({
                notifyUsers: false,
                plan: {
                  ...props.plan,
                  units: props.plan.units.concat(copy),
                },
              });

              setEditWorkoutUnit(createWorkoutUnit('', lastSortKey + 1));
              setShouldUpdateExerciseMetrics(true);
            }

            props.setShow(false);
          }}
        >
          {props.workoutUnit ? 'Update' : 'Create'}
        </Button>
      </Box>
    </Modal>
  );
};
